import type { Endpoints } from '@/generated/internal-api/Endpoints';
import { errorToast, infoToast } from '@/helpers';
import { _notNil } from '@/littledash';
import { ResponseType, type ApiResponse } from '@/support/ApiService';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import { useCallback } from 'react';

/**
 * The shape of the create-export response body the hook needs to fire-and-forget the
 * generate trigger. The hook is endpoint-agnostic so the caller's promise can be typed
 * to any of the AsyncExportEndpoint variants — they all share `{ api_id }` on success.
 */
type CreateResponse = { api_id?: string };

interface UseAsyncExportOptions {
  /**
   * Toast shown to the user once the export row is created and the generate trigger
   * has been fired. Defaults to the standard "see the Exports tab" copy; team-scoped
   * exports (no Exports tab) override with email-delivery copy.
   */
  successMessage?: string;
}

/**
 * Encapsulates the post-call work for every async-export call site:
 *   - awaits the create-export API call,
 *   - guards against unmount before reading the response,
 *   - surfaces an error toast on a non-Success response,
 *   - fire-and-forgets the legacy `exports.generate` trigger,
 *   - shows the queued-export toast.
 *
 * Call sites supply only the create call as a thunk so endpoint typing stays
 * site-local; the hook handles everything downstream.
 */
export const useAsyncExport = ({
  successMessage = 'Export queued — see the Exports tab when ready.',
}: UseAsyncExportOptions = {}) => {
  const isMounted = useMountedState();

  const { invoke: triggerExportGenerate } = useLegacyApiHook({
    method: 'POST',
    apiRoute: 'exports.generate',
    options: { invokeOnInit: false, onError: { toast: false, throw: false, slug: 'exports-generate' } },
  });

  return useCallback(
    async <Endpoint extends keyof Endpoints>(
      callApi: () => Promise<ApiResponse<Endpoint>>,
      // Per-call success message override — useful when one component drives multiple
      // export shapes (e.g. SamplesTable's study vs team variants).
      messageOverride?: string
    ): Promise<void> => {
      const response = await callApi();

      if (!isMounted()) {
        return;
      }

      if (response.type !== ResponseType.Success) {
        errorToast('Could not queue export');
        return;
      }

      const exportApiId = (response.body as CreateResponse | undefined)?.api_id;
      if (_notNil(exportApiId)) {
        triggerExportGenerate({ path: { exportApiId } }).catch(() => {
          // Fire-and-forget: failure is persisted on the row and visible in the Exports tab.
        });
      }

      infoToast(messageOverride ?? successMessage);
    },
    [isMounted, triggerExportGenerate, successMessage]
  );
};

export default useAsyncExport;
